import { useRecoilState } from "recoil";
import { userModelSelector } from "@/state/selectors/userModel";
import { signInMenuActivatedSelector } from "@/state/selectors/navbarToggle";
import { userHostModelSelector } from "@/state/selectors/userHostModel";
import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useCallback, useMemo } from "react";
import { useHomeScroll } from "@/contexts/HomeScrollContext";
import {
    AnyStaySidebarInsetContainer,
    AnyStaySidebarInsetInnerContainer,
    MainSidebarButtonIcon,
    MainSidebarLogoBtn,
    SidebarButton,
    SidebarButtonLink,
} from "./MainSidebarInsetStyling";
import AnystayLogoSidebar from "@/pages/dashboard/SideBar/AnystayLogoSidebar";
import { ColorTheme } from "../../globaStyledComponents/globalStyledComponentsStyling";

interface MainButtonsT {
    title: string;
    icon: string | React.ReactNode;
    matchPaths: string[],
    onClick: () => void;
}

const SHOW_LOGO_PATHS: string[] = ["/trips", "/saved", "/messages", "/messages-lite", "/profile", "/edit-profile", "/security-settings", "/payment-settings", "/payout-settings", "/search", "/listing"];

export default function MainSidebarInset() {
    const [userModel] = useRecoilState(userModelSelector)
    const [, setSignInMenuActivated] = useRecoilState(signInMenuActivatedSelector)
    const [userHostModel] = useRecoilState(userHostModelSelector)
    const { isHomeScrolled } = useHomeScroll();
    const navigate = useNavigate()
    const location = useLocation()
    
    const getInboxURL = useCallback(() => {
        if (userModel) {
            const convs = userModel.ConversationModels.items;
            const last: string = convs.length > 0 ? convs[convs.length - 1].id : "null";
            return `/messages/${last}`;
        }
        
        return "/messages-lite";
    }, [userModel]);
    
    // For each button, list *all* URL prefixes that count as "active"
    const mainButtons: MainButtonsT[] = useMemo(() => [
        {
            title: "Home",
            icon: "home",
            matchPaths: ["/"],
            onClick: () => navigate("/"),
        },
        {
            title: "Saved",
            icon: "sidebar_like.svg",
            matchPaths: ["/saved"],
            onClick: () => {
                const base: string  = `/saved${userModel?.id ? `/${userModel.id}` : ""}`;
                if (!userModel?.id) {
                    console.warn("Missing userId, can't go to saved");
                }
                
                navigate(base);
            },
        },
        {
            title: "Trips",
            icon: "sidebar_shop.svg",
            matchPaths: ["/trips"],
            onClick: () => {
                const base: string = `/trips${userModel?.id ? `/${userModel.id}` : ""}`;
                if (!userModel?.id) {
                    console.warn("Missing userId, can't go to trips");
                }
                
                navigate(base);
            },
        },
        {
            title: "Inbox",
            icon: "sidebar_chat.svg",
            matchPaths: ["/messages-lite", "/messages"],
            onClick: () => {
                navigate(getInboxURL());
            },
        },
        {
            title: "Profile",
            icon: "sidebar_user.svg",
            matchPaths: ["/profile", "/signin", "/edit-profile", "/security-settings", "/payment-settings", "/payout-settings"],
            onClick: () => {
                if (userModel) {
                    navigate("/profile");
                } else {
                    setSignInMenuActivated(true);
                }
            },
        },
    ], [navigate, setSignInMenuActivated, userModel, getInboxURL])
    
    // Compute selectedIndex by checking each button's array of prefixes.
    const selectedIndex: number = useMemo(() => {
        const idx: number = mainButtons.findIndex((btn: MainButtonsT) =>
          btn.matchPaths.some((path: string) => location.pathname === path || location.pathname.startsWith(path + "/"))
        );
        
        return idx >= 0 ? idx : 0;
    }, [location.pathname, mainButtons])
    
    // Determine logo & border the same as before.
    const showLogo: boolean = useMemo(() => {
        return SHOW_LOGO_PATHS.some((path: string) => location.pathname.startsWith(path));
    }, [location.pathname]);
    
    // Determine if user is a host with listings
    const isHostWithListings = useMemo(() => {
        return userHostModel && userHostModel.listingModelNumber && userHostModel.listingModelNumber > 0;
    }, [userHostModel]);
    
    return (
      <AnyStaySidebarInsetContainer>
          <div className="flex h-[96px] flex-row px-[12px] py-[12px]">
              <MainSidebarLogoBtn showLogo={showLogo} title="Home" onClick={() => navigate("/")}>
                  <AnystayLogoSidebar />
              </MainSidebarLogoBtn>
          </div>
          
          <AnyStaySidebarInsetInnerContainer showLogo={showLogo}>
                <div className="flex flex-col justify-center h-full pb-[80px] gap-4">
                  {mainButtons.map((btn: MainButtonsT, idx: number) => (
                    <SidebarButton
                      key={btn.title}
                      onClick={btn.onClick}
                      data-active={selectedIndex === idx}
                      title={btn.title}
                    >
                            {btn.icon === "home" ? (
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 26 26" 
                                    fill="none" 
                                    aria-label="Home" 
                                    role="img"
                                    style={{
                                        height: "24px",
                                        width: "24px",
                                        objectFit: "cover",
                                        border: "none"
                                    }}
                                >
                                    <title>Home</title>
                                    {selectedIndex === 0 ? (
                                        <path
                                            d="M2.25 12.8855V20.7497C2.25 21.8543 3.14543 22.7497 4.25 22.7497H8.25C8.52614 22.7497 8.75 22.5259 8.75 22.2497V17.6822V17.4997C8.75 15.1525 10.6528 13.2497 13 13.2497C15.3472 13.2497 17.25 15.1525 17.25 17.4997V17.6822V22.2497C17.25 22.5259 17.4739 22.7497 17.75 22.7497H21.75C22.8546 22.7497 23.75 21.8543 23.75 20.7497V12.8855C23.75 11.3765 23.0685 9.94815 21.8954 8.99883L16.1454 4.3454C14.3112 2.86095 11.6888 2.86095 9.85455 4.3454L4.10455 8.99883C2.93153 9.94815 2.25 11.3765 2.25 12.8855Z"
                                            fill={location.pathname === "/" && !isHomeScrolled ? ColorTheme.Grey1.color : ColorTheme.Black.color}
                                            stroke={location.pathname === "/" && !isHomeScrolled ? ColorTheme.Grey1.color : ColorTheme.Black.color}
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                        />
                                    ) : (
                                        <path
                                            d="M2.25 12.8855V20.7497C2.25 21.8543 3.14543 22.7497 4.25 22.7497H9.25C9.52614 22.7497 9.75 22.5258 9.75 22.2497V17.6822V16.4997C9.75 14.7048 11.2051 13.2497 13 13.2497C14.7949 13.2497 16.25 14.7048 16.25 16.4997V17.6822V22.2497C16.25 22.5258 16.4739 22.7497 16.75 22.7497H21.75C22.8546 22.7497 23.75 21.8543 23.75 20.7497V12.8855C23.75 11.3765 23.0685 9.94814 21.8954 8.99882L16.1454 4.34539C14.3112 2.86094 11.6888 2.86094 9.85455 4.34539L4.10455 8.99882C2.93153 9.94814 2.25 11.3765 2.25 12.8855Z"
                                            fill="none"
                                            stroke={location.pathname === "/" && !isHomeScrolled ? ColorTheme.Grey1.color : ColorTheme.Grey1.color}
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                        />
                                    )}
                                </svg>
                            ) : btn.icon === "sidebar_like.svg" ? (
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 26 26" 
                                    fill="none" 
                                    aria-label="Wishlists" 
                                    role="img"
                                    style={{
                                        height: "24px",
                                        width: "24px",
                                        objectFit: "cover",
                                        border: "none"
                                    }}
                                >
                                    <title>Wishlists</title>
                                    <path 
                                        d="M2.5 9.85683C2.5 14.224 6.22178 18.5299 12.0332 22.2032C12.3554 22.397 12.7401 22.5909 13 22.5909C13.2703 22.5909 13.655 22.397 13.9668 22.2032C19.7782 18.5299 23.5 14.224 23.5 9.85683C23.5 6.11212 20.8698 3.5 17.4599 3.5C15.4847 3.5 13.9356 4.39792 13 5.74479C12.0851 4.40812 10.5257 3.5 8.5401 3.5C5.14059 3.5 2.5 6.11212 2.5 9.85683Z" 
                                        fill={selectedIndex === 1 ? ColorTheme.Black.color : "transparent"}
                                        stroke={selectedIndex === 1 ? ColorTheme.Black.color : ColorTheme.Grey1.color} 
                                        strokeWidth="2.5" 
                                    />
                                </svg>
                            ) : btn.icon === "sidebar_shop.svg" ? (
                                <svg 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 27 28" 
                                    fill="none" 
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-label="Trips" 
                                    role="img"
                                    style={{
                                        height: "24px",
                                        width: "24px",
                                        objectFit: "cover",
                                        border: "none"
                                    }}
                                >
                                    <title>Trips</title>
                                    <path 
                                        d="M17.8365 11.5942C17.8365 13.8033 15.8592 15.5942 13.42 15.5942C10.9808 15.5942 9.0035 13.8033 9.0035 11.5942M17.8365 7.59417V5.87988C17.8365 3.67074 15.8592 1.87988 13.42 1.87988C10.9808 1.87988 9.00351 3.67074 9.00351 5.87988V7.59417M9.49147 25.8799H17.3486C21.7158 25.8799 25.1774 22.5426 24.905 18.595L24.2937 9.7373C24.2106 8.53265 23.1076 7.59417 21.7749 7.59417H5.06512C3.73243 7.59417 2.62944 8.53265 2.54631 9.7373L1.93506 18.595C1.66264 22.5426 5.12427 25.8799 9.49147 25.8799Z" 
                                        stroke={selectedIndex === 2 ? ColorTheme.Black.color : ColorTheme.Grey1.color}
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            ) : btn.icon === "sidebar_chat.svg" ? (
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 24 23" 
                                    fill="none" 
                                    aria-label="Messages" 
                                    role="img"
                                    style={{
                                        height: "24px",
                                        width: "24px",
                                        objectFit: "cover",
                                        border: "none"
                                    }}
                                >
                                    <title>Messages</title>
                                    <path 
                                        d="M18.68 1H5.32C4.30687 1 3.33524 1.38287 2.61885 2.06439C1.90246 2.7459 1.5 3.67023 1.5 4.63404V13.7096C1.5 14.6734 1.90246 15.5978 2.61885 16.2793C3.33524 16.9608 4.30687 17.3437 5.32 17.3437H9.14L12 21L14.86 17.3437H18.68C19.6931 17.3437 20.6648 16.9608 21.3811 16.2793C22.0975 15.5978 22.5 14.6734 22.5 13.7096V4.63404C22.5 3.67023 22.0975 2.7459 21.3811 2.06439C20.6648 1.38287 19.6931 1 18.68 1Z" 
                                        stroke={selectedIndex === 3 ? ColorTheme.Black.color : ColorTheme.Grey1.color} 
                                        strokeWidth="2.2" 
                                        strokeMiterlimit="10"
                                    />
                                </svg>
                            ) : btn.icon === "sidebar_user.svg" ? (
                                <svg 
                                    aria-label="Profile" 
                                    role="img" 
                                    viewBox="0 0 26 26" 
                                    style={{
                                        height: "24px",
                                        width: "24px",
                                        objectFit: "cover",
                                        border: "none"
                                    }}
                                >
                                    <title>Profile</title>
                                    <circle 
                                        cx="13" 
                                        cy="7.25" 
                                        r="4" 
                                        fill={selectedIndex === 4 ? ColorTheme.Black.color : "none"}
                                        stroke={selectedIndex === 4 ? ColorTheme.Black.color : ColorTheme.Grey1.color} 
                                        strokeWidth="2.5"
                                    />
                                    <path 
                                        d="M6.26678 23.75H19.744C21.603 23.75 22.5 23.2186 22.5 22.0673C22.5 19.3712 18.8038 15.75 13 15.75C7.19625 15.75 3.5 19.3712 3.5 22.0673C3.5 23.2186 4.39704 23.75 6.26678 23.75Z" 
                                        fill={selectedIndex === 4 ? ColorTheme.Black.color : "none"}
                                        stroke={selectedIndex === 4 ? ColorTheme.Black.color : ColorTheme.Grey1.color} 
                                        strokeWidth="2.5"
                                    />
                                </svg>
                            ) : (
                                <MainSidebarButtonIcon
                                    data-active={selectedIndex === idx}
                                    className="scale-[1.714]"
                                    src={`https://d292awxalydr86.cloudfront.net/Home+page/V4+assets/icon/${btn.icon}`}
                                    alt={btn.title}
                                />
                            )}
                    </SidebarButton>
                  ))}
              </div>
              
              <div className="flex flex-col mb-2">
                  <SidebarButtonLink 
                    title={isHostWithListings ? "Switch to hosting" : "List your place"}
                    onClick={isHostWithListings ? () => navigate("/dashboard") : () => navigate("/list-your-place")}
                  >
                      {isHostWithListings ? (
                          <img
                            className="w-[20px] h-[20px]"
                            src="https://d292awxalydr86.cloudfront.net/Universal+icons/Group.svg"
                            alt="Switch to hosting"
                          />
                      ) : (
                          <img
                            className="w-[24px] h-[24px]"
                            src="https://d292awxalydr86.cloudfront.net/Home+page/V4+assets/icon/sidebar_add.svg"
                            alt="Add"
                          />
                      )}
                  </SidebarButtonLink>
              </div>
          </AnyStaySidebarInsetInnerContainer>
      </AnyStaySidebarInsetContainer>
    )
}